import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { Observable } from 'rxjs';
import { map, tap, filter, switchMap, startWith } from 'rxjs/operators';

import { SharedKeys, SharedKeysMap, SharedKeysEnum, } from '@library/common/models';

import { ReportListService } from '../report.list.service';
import { Preferences, SearchService, BreakpointService, KeyboardShortcutsService } from '@library/core/services';

import { ReportListItem } from './report-list-item';

@Component({
  selector: 'lib-reports-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements OnInit, OnDestroy {

   shared_key: SharedKeys | '' = '';
   SharedKeysMap = SharedKeysMap;
   SharedKeysEnum = SharedKeysEnum;

   list: Observable<ReportListItem[]>;
   dataSource: MatTableDataSource<ReportListItem> = new MatTableDataSource();

   pinning: number;
   _deleting: ReportListItem;
   deleted: ReportListItem;

   @ViewChild(MatPaginator) paginator: MatPaginator;
   @ViewChild(MatSort) sort: MatSort;

   @ViewChild('deleteConfirm') deleteConfirm: ElementRef;

   get pageSize() { return this.prefs.getPageSize( 'reports' ); }
   set pageSize( value ) { this.prefs.setPageSize( 'reports', value ); }
   get reportStatus() { return this.prefs.getPref('reports.status'); }

   constructor(
      private router: Router,
      private snackbar: MatSnackBar,

      private prefs: Preferences,
      private search: SearchService,
      private ks: KeyboardShortcutsService,

      private reportList: ReportListService,

      public breakpoints: BreakpointService,
   ) {
      this.search.showSearch = true;
      this.search.params.get( 'text' ).valueChanges.subscribe( change => this.dataSource.filter = change );
   }

   ngOnInit() {
      this.ks.register$({
         description: 'New Report',
         key: 'N',
         keyDisplay: 'Shift + N',
         icon: 'add',
      }).subscribe( _ => this.router.navigate([ '/report/new' ]));

      this.list = this.reportList.refresh$.pipe(
         startWith( true ),
         switchMap( _ => this.reportList.list() ),
         // filter by shared key
         map( list => list.filter( r => this.reportStatus ? this.reportStatus === SharedKeysMap( r ) : true )),
         // filter by deleted
         map( list => list.filter( r => this.deleted ? r.id !== this.deleted.id : true )),
         tap( list => this.dataSource.data = list ),
         tap( _ => setTimeout(() => {
               this.dataSource.paginator = this.paginator;
               this.dataSource.sort = this.sort;
            }, 1 )),
      );
   }

   ngOnDestroy() {
      this.ks.remove( 'N' );
   }

   toggle_shared_key( status: SharedKeys ): void {
      this.prefs.upsertPref( 'reports.status', ( this.reportStatus === status ) ? '' : status );
      this.reportList.refresh$.next();
   }

   get deleting() { return this._deleting; }
   set deleting( value: ReportListItem ) {
      this._deleting = value;
      if ( value == null ) {
         this.reportList.refresh$.next();

         return;
      }
      this.dataSource.data = this.dataSource.data.filter( r => r.id === value.id );
      setTimeout( _ => this.deleteConfirm.nativeElement.scrollIntoView() );
   }

   delete( report: ReportListItem ): void {
      this.deleted = report;
      this.deleting = null;

      const snackbarRef = this.snackbar.open( report.name + ' -- Deleted', 'Undo', {
         duration: 5000, verticalPosition: 'bottom'
      });

      snackbarRef.afterDismissed().subscribe( dismiss => {
         if ( dismiss.dismissedByAction ) {
            this.deleted = null;
            this.reportList.refresh$.next();
         } else {
            this.reportList.delete( report.id ).subscribe( _ => this.deleted = null );
         }
      });
   }

   pin( id: number ): void {
      this.pinning = id;
      this.reportList.pin( id ).pipe(
         filter( result => !!result ),
      ).subscribe( _ => {
         this.reportList.refresh$.next();
         this.pinning = null;
      });
   }

   unpin( id: number ): void {
      this.pinning = id;
      this.reportList.unpin( id ).pipe(
         filter( result => !!result ),
      ).subscribe( _ => {
         this.reportList.refresh$.next();
         this.pinning = null;
      });
   }
}
